'use client'

import { useSyncExternalStore } from 'react'

/**
 * The mood filter's client-side navigation contract (blog-moods FR-8, FR-9, RD-15).
 *
 * Selecting a mood on the blog home is **not** a route change: it sets a
 * `?mood=<slug>` query param and the blog home swaps to a mood-only view. The
 * page stays a single **static** `/blog` surface — every `?mood=` variant serves
 * the same server HTML with the same `/blog/` canonical, so no mood variant is
 * indexed and no crawlable per-mood URL exists.
 *
 * - Entering a mood uses **`pushState`** (not `replaceState`) so the browser Back
 *   button returns to the homepage, and stamps `{ fromBlogHome: true }` on the
 *   entry so "Back to blog" knows a homepage entry sits behind it.
 * - The active slug is read from the URL via `useSyncExternalStore` with a `null`
 *   **server snapshot**, so SSR/first-hydration render the homepage (never a mood
 *   view) and a shared `/blog?mood=<slug>` link opens the filter after hydration.
 */

export const MOOD_PARAM = 'mood'

/**
 * The collection filter rides the same mechanism (blog FR-13b): `?collection=all`
 * opens the all-stories view exactly the way `?mood=<slug>` opens a mood view.
 * Mood and collection are mutually exclusive — opening either clears the other's
 * param, and a hand-crafted URL naming both renders the mood view
 * (`BlogHomeContent` checks mood first).
 */
export const COLLECTION_PARAM = 'collection'
export const ALL_STORIES_COLLECTION = 'all'

// Fired after we rewrite the param so the store re-reads it (pushState/replaceState
// do not emit popstate on their own).
const MOOD_CHANGE_EVENT = 'wanderu:mood-change'

interface MoodHistoryState {
    fromBlogHome?: boolean
}

const subscribe = (onChange: () => void) => {
    window.addEventListener('popstate', onChange)
    window.addEventListener(MOOD_CHANGE_EVENT, onChange)
    return () => {
        window.removeEventListener('popstate', onChange)
        window.removeEventListener(MOOD_CHANGE_EVENT, onChange)
    }
}

const getSnapshot = () => new URLSearchParams(window.location.search).get(MOOD_PARAM)

const getCollectionSnapshot = () =>
    new URLSearchParams(window.location.search).get(COLLECTION_PARAM)

/** The mood slug in `?mood=`, or `null` (and always `null` on the server). */
export const useActiveMoodSlug = (): string | null =>
    useSyncExternalStore(subscribe, getSnapshot, () => null)

/** The collection slug in `?collection=`, or `null` (and always `null` on the server). */
export const useActiveCollectionSlug = (): string | null =>
    useSyncExternalStore(subscribe, getCollectionSnapshot, () => null)

/** Shared open path: rewrite the params, push (so Back returns), notify, scroll up. */
const openFilter = (set: (params: URLSearchParams) => void): void => {
    const url = new URL(window.location.href)
    set(url.searchParams)
    window.history.pushState({ fromBlogHome: true } satisfies MoodHistoryState, '', url)
    window.dispatchEvent(new Event(MOOD_CHANGE_EVENT))
    // The entry points sit low on the page; the view replaces them, so start it
    // at the top rather than mid-scroll where the control was clicked.
    window.scrollTo(0, 0)
}

/**
 * Enter a mood: push a `?mood=<slug>` history entry (so Back returns to the
 * homepage) and notify the store. Same path, no navigation. Clears any active
 * collection — the two views are exclusive.
 */
export const openMood = (slug: string): void => {
    openFilter((params) => {
        params.set(MOOD_PARAM, slug)
        params.delete(COLLECTION_PARAM)
    })
}

/**
 * Enter a collection view (today only `all` — the all-stories view, blog
 * FR-13b): same contract as {@link openMood}. Clears any active mood.
 */
export const openCollection = (slug: string): void => {
    openFilter((params) => {
        params.set(COLLECTION_PARAM, slug)
        params.delete(MOOD_PARAM)
    })
}

/**
 * Leave the mood view. When the mood was opened in this session (the current
 * entry is stamped `fromBlogHome`), a real Back restores the homepage entry and
 * its scroll. Otherwise — a cold shared `?mood=` visit with no homepage entry
 * behind — navigate to `/blog/` (a full load that re-renders the homepage; the
 * blog serves at its un-prefixed canonical, so no locale hop). Trailing slash
 * included: `trailingSlash: true` would otherwise `308` the bare form, making
 * the reader pay a redirect on the way back
 * (`invariants.md#rendered-links-are-canonical`).
 */
export const backToBlog = (): void => {
    const state = window.history.state as MoodHistoryState | null
    if (state?.fromBlogHome) {
        window.history.back()
    } else {
        window.location.assign('/blog/')
    }
}
